import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { getOperatorMessage, type OperatorMessage } from 'api/workflows/endpoints/getOperatorMessage';
import { toError } from 'helpers/toError';
import { MobxQuery } from 'mobx/MobxQuery';
import { respondToWorkflowAction } from 'redux/slices/workflowsSlice';
import type { AppDispatch } from 'redux/store';
import { refuseDegraded } from 'validation';

/**
 * MobX-backed behaviour for the operator message panel in the node instance view: one workflow, read by its id, and
 * the operator's response to it.
 *
 * The id is fixed for the model's lifetime, so a panel showing another workflow needs another model.
 */
export class OperatorMessageModel {
  private readonly _dispatch: AppDispatch;
  /** How the last response went, until the operator dismisses it or responds again. */
  private _outcome: null | 'responded' | Error = null;
  private readonly _query: MobxQuery<number, OperatorMessage | null>;
  /** The action whose response is in flight. */
  private _responding: number | null = null;

  constructor(workflowId: number, dispatch: AppDispatch) {
    this._dispatch = dispatch;
    // The operator responds to what this shows, so a message that could not be read in full is refused, not shown.
    this._query = new MobxQuery({ params: () => workflowId, fetch: (id: number) => getOperatorMessage(id).then(refuseDegraded), debounceMs: 0 });
    makeObservable<OperatorMessageModel, '_outcome' | '_responding'>(this, {
      _outcome: observable.ref,
      _responding: observable,
      busy: computed,
      dismissOutcome: action,
      message: computed,
    });
  }

  /**
   * True while a response or the re-read after it is in flight. What is shown then may still offer an action that has
   * just been answered, and the respond route does not refuse a second answer: it overwrites the first and moves the
   * flow again.
   */
  get busy(): boolean {
    return this._responding !== null || this._query.value.kind === 'stale';
  }

  /**
   * What the panel has to show. A failed read is an `Error` even when an earlier answer is held: the failure is nearly
   * always the re-read after a response, so that answer predates it and would offer the action just answered, without
   * `busy` to hold its buttons. A re-read in flight keeps showing the message; `busy` covers it.
   */
  get message(): 'loading' | 'not-found' | Error | OperatorMessage {
    const value = this._query.value;
    switch (value.kind) {
      case 'uninitialized':
      case 'loading':
        return 'loading';
      case 'error':
        return value.error;
      case 'resolved':
        return value.answer.value ?? 'not-found';
      case 'stale':
        return value.previous.value ?? 'not-found';
    }
  }

  get outcome(): null | 'responded' | Error {
    return this._outcome;
  }

  get responding(): number | null {
    return this._responding;
  }

  dismissOutcome(): void {
    this._outcome = null;
  }

  async respond(actionId: number, chosenId: string): Promise<void> {
    runInAction(() => {
      this._responding = actionId;
      this._outcome = null;
    });
    try {
      await this._dispatch(respondToWorkflowAction({ workflow_action_id: actionId, chosen_id: chosenId })).unwrap();
      runInAction(() => (this._outcome = 'responded'));
      // The node instance list is not reachable from here; it listens for this to refresh.
      window.dispatchEvent(new CustomEvent('operator-message-responded'));
      void this._query.refresh();
    } catch (err) {
      runInAction(() => (this._outcome = toError(err, 'Failed to submit response.')));
    } finally {
      runInAction(() => (this._responding = null));
    }
  }
}
